import { supabase } from "./supabase";
export const CONTENT_KINDS = [
  {
    kind: "gpt",
    label: "맞춤 GPT",
    hint: "글 안에서 직접 대화",
    route: "/workmate/custom-gpt",
  },
  {
    kind: "quiz",
    label: "스피드 퀴즈 배틀",
    hint: "내 공개 문제 세트",
    route: "/classmate/quiz-battle",
  },
  {
    kind: "wordcloud",
    label: "워드클라우드",
    hint: "실제 단어 구름",
    route: "/classmate/wordcloud",
  },
  {
    kind: "peerquiz",
    label: "학생 문제 배틀",
    hint: "활동에서 고른 문제",
    route: "/classmate/peer-quiz/host",
  },
  {
    kind: "sociometry",
    label: "마음관계분석",
    hint: "가명 관계도와 집계",
    route: "/classmate/sociometry",
  },
  {
    kind: "prompt",
    label: "AI 파일 수합",
    hint: "내 점검 프롬프트",
    route: "/workmate/file-inbox",
  },
  {
    kind: "book",
    label: "교사의 책",
    hint: "내 책 소개",
    route: "/informate/books",
  },
  {
    kind: "vibe",
    label: "바이브코딩",
    hint: "내가 만든 웹사이트",
    route: "/informate/vibe-sites",
  },
] as const;
export type ContentKind = (typeof CONTENT_KINDS)[number]["kind"];
export type ContentQuestion = {
  key?: string;
  text: string;
  answer: string;
  choices: string[];
  choiceImages?: string[];
  image?: string;
  round?: number;
};
export type ContentData = {
  title: string;
  description?: string;
  examples?: string[];
  prompt?: string;
  image?: string;
  url?: string;
  author?: string;
  questions?: ContentQuestion[];
  words?: { key?: string; text: string; count: number }[];
  nodes?: { id: string; label: string }[];
  edges?: { from: string; to: string; kind: string }[];
  mood?: { value: number; count: number }[];
  note?: string;
};
export type ContentSelection = {
  title?: string;
  keys?: string[];
  relations?: string[];
  includeMood?: boolean;
  reviewed?: boolean;
  prompt?: string;
};
export type CommunityContentBlock = {
  id: string;
  kind: ContentKind;
  mode: "reference" | "snapshot";
  sourceId?: string;
  version: 1;
  data: ContentData;
  href?: string;
  ticket?: string;
  selection?: ContentSelection;
  unavailable?: boolean;
};
export const RELATION_LABELS: Record<string, string> = {
  intimacy: "친밀감",
  task_trust: "과제 신뢰",
  positive_energy: "긍정 에너지",
  academic_support: "학습 도움",
  future_relation: "다가가고 싶은 관계",
  opinion_leader: "의견 영향",
  mediator: "중재",
  isolation_concern: "고립 우려",
  conflict: "갈등",
  distance: "거리감",
};
export async function contentRequest<T>(
  action: string,
  params: Record<string, unknown> = {},
  method: "GET" | "POST" = "GET",
): Promise<T> {
  const token = (await supabase?.auth.getSession())?.data.session?.access_token;
  const query = new URLSearchParams({
    action,
    ...(method === "GET"
      ? Object.fromEntries(
          Object.entries(params).map(([k, v]) => [k, String(v)]),
        )
      : {}),
  });
  const r = await fetch(`/api/community/content?${query}`, {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(method === "POST" ? { "Content-Type": "application/json" } : {}),
    },
    ...(method === "POST" ? { body: JSON.stringify(params) } : {}),
  });
  const data = await r.json().catch(() => null);
  if (!r.ok) throw new Error(data?.error || "콘텐츠를 불러오지 못했습니다.");
  return data;
}
export function selectedData(block: CommunityContentBlock): ContentData {
  const { data, selection: s } = block;
  if (!s || block.mode === "reference") return data;
  const keys = new Set(s.keys || []);
  const nodes = data.nodes?.filter((n) => keys.has(n.id));
  const ids = new Set(nodes?.map((n) => n.id));
  return {
    ...data,
    title: s.title || data.title,
    ...(block.kind === "prompt" ? { prompt: s.prompt } : {}),
    ...(data.words
      ? { words: data.words.filter((w) => keys.has(w.key!)) }
      : {}),
    ...(data.questions
      ? { questions: data.questions.filter((q) => keys.has(q.key!)) }
      : {}),
    ...(nodes
      ? {
          nodes,
          edges: data.edges?.filter(
            (e) =>
              ids.has(e.from) && ids.has(e.to) && s.relations?.includes(e.kind),
          ),
          mood: s.includeMood ? data.mood : [],
        }
      : {}),
  };
}
export function initialSelection(
  block: CommunityContentBlock,
): ContentSelection {
  const d = block.data;
  return {
    title: d.title,
    prompt: d.prompt,
    keys:
      block.kind === "sociometry"
        ? d.nodes?.map((n) => n.id)
        : block.kind === "wordcloud"
          ? d.words?.map((w) => w.key!)
          : d.questions?.map((q) => q.key!),
    relations: ["intimacy"],
    includeMood: false,
    reviewed: false,
  };
}
export function blockMarker(block: CommunityContentBlock) {
  return `<div data-community-block="${block.id}" contenteditable="false"><p>연결 콘텐츠 · ${CONTENT_KINDS.find((k) => k.kind === block.kind)?.label || "내 콘텐츠"}</p><p>콘텐츠를 지원하는 소통공간 화면에서 확인할 수 있습니다.</p></div>`;
}
export type BodyPart =
  { type: "text"; html: string } | { type: "block"; id: string; html: string };
export function splitContent(html: string): BodyPart[] {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const parts: BodyPart[] = [];
  const nodes = [
    ...doc.body.querySelectorAll<HTMLElement>("[data-community-block]"),
  ].filter((n) => !n.parentElement?.closest("[data-community-block]"));
  let range = doc.createRange();
  range.setStart(doc.body, 0);
  const fragmentHtml = (f: DocumentFragment) => {
    const div = doc.createElement("div");
    div.append(f);
    return div.innerHTML;
  };
  for (const n of nodes) {
    range.setEndBefore(n);
    const before = fragmentHtml(range.cloneContents());
    if (before) parts.push({ type: "text", html: before });
    parts.push({
      type: "block",
      id: n.dataset.communityBlock || "",
      html: n.outerHTML,
    });
    range = doc.createRange();
    range.setStartAfter(n);
  }
  range.setEnd(doc.body, doc.body.childNodes.length);
  const tail = fragmentHtml(range.cloneContents());
  if (tail || !parts.length) parts.push({ type: "text", html: tail });
  return parts;
}
export function activeBlocks(html: string, blocks: CommunityContentBlock[]) {
  const ids = new Set(
    splitContent(html)
      .filter((p) => p.type === "block")
      .map((p) => p.id),
  );
  return blocks.filter((b) => ids.has(b.id));
}
